import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Globe, X } from "lucide-react";
import api from "./api";

export const T = {
  bg: "#1F1E1B",
  surface: "#26241F",
  surface2: "#2E2B25",
  accent: "#B5A58A",
  success: "#637A61",
  warning: "#B37D3B",
  line: "rgba(255,255,255,0.08)",
};

export function Spinner({ label = "Thinking…" }) {
  return (
    <div className="flex items-center gap-3 py-10 justify-center" data-testid="spinner">
      <span className="w-4 h-4 rounded-full border-2 border-[#B5A58A]/30 border-t-[#B5A58A] animate-spin" />
      <span className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-stone-500">{label}</span>
    </div>
  );
}

export function Pill({ children, tone = "neutral", testid }) {
  const tones = {
    neutral: "border-white/15 text-stone-400",
    accent: "border-[#B5A58A]/40 text-[#B5A58A]",
    success: "border-[#637A61] text-[#8FA98D]",
    warning: "border-[#B37D3B]/60 text-[#D09B5F]",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1 font-mono-label text-[9px] tracking-[0.2em] uppercase ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export function Btn({ children, variant = "primary", className = "", testid, ...rest }) {
  const styles = {
    primary:
      "bg-[#B5A58A] text-[#1F1E1B] hover:bg-[#c4b59a] disabled:opacity-40",
    outline:
      "border border-white/15 text-stone-300 hover:border-[#B5A58A]/60 hover:text-white",
    ghost: "text-stone-400 hover:text-white",
  };
  return (
    <button
      data-testid={testid}
      className={`rounded-full px-6 py-3 font-mono-label text-[10px] tracking-[0.25em] uppercase transition-colors duration-300 ${styles[variant]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Modal({ open, onClose, children, testid }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 px-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid={testid}
            className="relative w-full max-w-lg bg-[#26241F] border border-white/[0.08] rounded-2xl p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="modal-close-btn"
              onClick={onClose}
              className="absolute top-5 right-5 text-stone-500 hover:text-white transition-colors duration-300"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function relTime(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function StatusPill({ status }) {
  const map = {
    OPENED: ["neutral", "Opened"],
    INVESTIGATING: ["accent", "In review"],
    ANALYSIS_READY: ["accent", "Ready"],
    ANALYZED: ["accent", "Analyzed"],
    DECIDED: ["success", "Decided"],
    ACTION_PLAN: ["success", "In action"],
    REVIEW_DUE: ["warning", "Review due"],
    RESOLVED: ["success", "Resolved"],
    PAUSED: ["neutral", "Paused"],
    ARCHIVED: ["neutral", "Archived"],
  };
  const [tone, label] = map[status] || ["neutral", status || "Opened"];
  return <Pill tone={tone} testid={`status-${(status || "").toLowerCase()}`}>{label}</Pill>;
}

export function VisibilityBadge({ kase, onChange, isGuest = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isPublic = kase.visibility !== "PRIVATE";

  const patch = async (visibility) => {
    setBusy(true);
    try {
      const r = await api.patch(`/cases/${kase.id}`, { visibility });
      onChange?.(r.data);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="visibility-badge"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 border rounded-full px-3 py-1 font-mono-label text-[9px] tracking-[0.2em] uppercase transition-colors duration-300 ${
          isPublic ? "border-[#637A61] text-[#8FA98D]" : "border-white/15 text-stone-400"
        }`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${isPublic ? "bg-[#637A61]" : "bg-stone-500"}`} />
        {isPublic ? "Public" : "Private"}
      </button>

      <Modal open={open} onClose={() => setOpen(false)} testid="visibility-modal">
        {isGuest ? (
          <>
            <div className="w-12 h-12 rounded-full bg-[#B5A58A]/15 flex items-center justify-center mb-5">
              <Globe className="w-5 h-5 text-[#B5A58A]" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif-display font-light tracking-tight text-3xl text-white mb-4">
              Always public as a guest.
            </h3>
            <div className="space-y-3 text-sm leading-relaxed text-stone-400 mb-8">
              <p>Guest cases are public by design — anonymized by AI and posted to the public column the moment they open, so other people can weigh in.</p>
              <p>Your identity is never attached. But if you want a case to stay just yours, create a free account — your cases carry over, and the privacy switch comes with it.</p>
            </div>
            <div className="flex flex-col gap-3">
              <Btn testid="guest-create-account-btn" onClick={() => { window.location.href = "/auth?mode=register"; }}>Create a free account</Btn>
              <Btn testid="guest-got-it-btn" variant="outline" onClick={() => setOpen(false)}>Got it</Btn>
            </div>
          </>
        ) : isPublic ? (
          <>
            <div className="w-12 h-12 rounded-full bg-[#B5A58A]/15 flex items-center justify-center mb-5">
              <ShieldCheck className="w-5 h-5 text-[#B5A58A]" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif-display font-light tracking-tight text-3xl text-white mb-4">
              You are already anonymous.
            </h3>
            <div className="space-y-3 text-sm leading-relaxed text-stone-400 mb-8">
              <p>Cases in the public column are deeply anonymized — names, companies, cities, and identifying details are all scrubbed by AI before anyone can see them.</p>
              <p>Keeping this case public means someone who has lived through something similar may weigh in with a perspective you could not have reached alone. Your case can also quietly help someone else who is stuck.</p>
              <p>If you still want this one to yourself, that is fine. It will be removed from the public column, and no one else will see it or comment on it.</p>
            </div>
            <div className="flex flex-col gap-3">
              <Btn testid="keep-public-btn" onClick={() => setOpen(false)}>Keep it public</Btn>
              <Btn testid="make-private-btn" variant="outline" disabled={busy} onClick={() => patch("PRIVATE")}>
                Make private anyway
              </Btn>
            </div>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-[#B5A58A]/15 flex items-center justify-center mb-5">
              <Globe className="w-5 h-5 text-[#B5A58A]" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif-display font-light tracking-tight text-3xl text-white mb-4">
              Publish to the public column?
            </h3>
            <p className="text-sm leading-relaxed text-stone-400 mb-8">
              Your identity stays private — names, companies, locations, and specifics are anonymized before anyone sees the case. Publishing lets others upvote and comment with a perspective.
            </p>
            <div className="flex flex-col gap-3">
              <Btn testid="publish-anon-btn" disabled={busy} onClick={() => patch("PUBLIC")}>Publish anonymously</Btn>
              <Btn testid="cancel-publish-btn" variant="outline" onClick={() => setOpen(false)}>Cancel</Btn>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
